import React, { useCallback, useEffect } from "react";
import {
  Field,
  Form,
  FormElement,
  FormRenderProps,
} from "@progress/kendo-react-form";
import { GridLayout, GridLayoutItem } from "@progress/kendo-react-layout";
import { useAppDispatch, useAppSelector } from "../../../app/hooks";
import ButtonWithLoading from "../../../components/common/ButtonWithLoading";
import { Typography } from "@progress/kendo-react-common";
import ShadowCard from "../../../components/common/ShadowCard";
import {
  clearItemDetailsRegisterReportDetails,
  clearItemDetailsRegisterReportList,
  setItemDetailsRegisterReport,
} from "../reportsSlice";
import AlertBox from "../../../components/common/AlertBox";
import { LoadingPanel } from "../../../components/layout/Loading";
import { Button } from "@progress/kendo-react-buttons";
import {
  checkAcessRights,
  formatIndianNumberForQty,
} from "../../../_helper/helper";
import { EXCEL_EXPORT_ACCESS, PRINT_ACCESS } from "../../../_contstants/common";
import { getAllItemDetailsRegisterReport } from "../services/reports.services";
import { Tooltip } from "@progress/kendo-react-tooltip";
import { BsThreeDotsVertical } from "react-icons/bs";
import { RxCross2 } from "react-icons/rx";
import FormCheckboxGroup from "../../../components/formFields/FormCheckboxGroup";
import * as XLSX from "xlsx";
import { saveAs } from "file-saver";
import moment from "moment";
import FormDatePicker from "../../../components/formFields/FormDateField";
import FormIncrementalSearch from "../../../components/formFields/FormIncrementalSearch";
import {
  getAllItemIncremental,
  getGRNByOnlyItemID,
} from "../../Item/services/item.services";
import FormSelectionField from "../../../components/formFields/FormSelectionField";
import { requiredArrayValidator } from "../../../components/formFields/CommonValidator";

interface FormChangeWatcherProps {
  formRenderProps: FormRenderProps;
}
const ChangeWatcher = ({ formRenderProps }: FormChangeWatcherProps) => {
  useEffect(() => {
    formRenderProps.onChange("abcd", { value: "forenable" });
  }, []);

  return null;
};

const ItemChangeWatcher: React.FC<FormChangeWatcherProps> = ({
  formRenderProps,
}) => {
  const dispatch = useAppDispatch();
  const item_id = formRenderProps.valueGetter("item_id");

  useEffect(() => {
    const fetchGRNOptions = async () => {
      formRenderProps.onChange("grn_no", {
        value: null,
      });
      if (item_id) {
        const response: any = await dispatch(getGRNByOnlyItemID(item_id));
        if (response && response.payload) {
          const result = response.payload
            .filter((grn: any) => grn?.item_id === item_id)
            .map((grnno: any) => ({
              value: `${grnno?.grn_no}`,
              label: `${grnno?.grn_no}`,
            }));

          formRenderProps.onChange("grnNoOptions", {
            value: result?.length > 0 && result,
          });
        } else {
          formRenderProps.onChange("grnNoOptions", { value: [] });
          formRenderProps.onChange("grn_no", {
            value: null,
          });
        }
      } else {
        formRenderProps.onChange("grnNoOptions", { value: [] });
        formRenderProps.onChange("grn_no", {
          value: null,
        });
      }
    };

    fetchGRNOptions();
  }, [item_id]);

  return null;
};

const ItemDetailsRegisterReportForm: React.FC = () => {
  const gridRef = React.useRef<any>(null);
  const dispatch = useAppDispatch();
  const loading = useAppSelector((state) => state.reports.loading);

  const ItemDetailsRegisterReportDetails = useAppSelector(
    (state) => state.reports.ItemDetailsRegisterReportDetails
  );
  const ItemDetailsRegisterReportList = useAppSelector(
    (state) => state.reports.ItemDetailsRegisterReportList
  );

  useEffect(() => {
    return () => {
      dispatch(clearItemDetailsRegisterReportDetails());
      dispatch(clearItemDetailsRegisterReportList());
    };
  }, []);

  const handleItemSearchChange = useCallback(
    async (search: string, field: string, formRenderProps: FormRenderProps) => {
      const result = await dispatch(getAllItemIncremental({ search }));
      formRenderProps.onChange(field, {
        value: result.payload,
      });
    },
    [dispatch]
  );

  const handleSubmit = async (values: any) => {
    const payload = {
      item_id: values?.item_id ? values?.item_id : null,
      grn_no: values?.grn_no ? `${values?.grn_no}` : null,
      from_date: values?.from_date
        ? moment(values?.from_date).format("YYYY-MM-DD")
        : "",
      to_date: values?.to_date
        ? moment(values?.to_date).format("YYYY-MM-DD")
        : "",
    };
    const setPayload = {
      item_id: values?.item_id ? values?.item_id : null,
      grn_no: values?.grn_no ? `${values?.grn_no}` : "",
      from_date: values?.from_date,
      to_date: values?.to_date,
      item_options: values?.item_options || [],
    };
    dispatch(setItemDetailsRegisterReport(setPayload));
    await dispatch(getAllItemDetailsRegisterReport(payload));
  };

  return (
    <GridLayout gap={{ rows: 10 }} cols={[{ width: "100%" }]}>
      <GridLayoutItem>
        <ShadowCard>
          <Form
            initialValues={ItemDetailsRegisterReportDetails}
            onSubmit={handleSubmit}
            render={(formRenderProps: FormRenderProps) => (
              <FormElement style={{ width: "100%" }}>
                <GridLayout
                  style={{ marginRight: 30 }}
                  gap={{ rows: 10, cols: 10 }}
                  cols={[
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                    { width: "25%" },
                  ]}
                >
                  <ChangeWatcher formRenderProps={formRenderProps} />
                  <ItemChangeWatcher formRenderProps={formRenderProps} />
                  <GridLayoutItem colSpan={4}>
                    <Typography.h4
                      style={{ marginBottom: 0, marginLeft: "3px" }}
                    >
                      Item Details Register Report
                    </Typography.h4>
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name="from_date"
                      label="From Date"
                      format={"dd/MM/yyyy"}
                      component={FormDatePicker}
                    />
                  </GridLayoutItem>

                  <GridLayoutItem>
                    <Field
                      wrapperStyle={{ width: "100%" }}
                      name="to_date"
                      label="To Date"
                      format={"dd/MM/yyyy"}
                      minDate={
                        formRenderProps.valueGetter("from_date") ||
                        new Date(1900, 1, 1)
                      }
                      component={FormDatePicker}
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      wrapperClassName="w-100"
                      name="item_id"
                      label="Product Name"
                      placeholder="Type here to search item..."
                      component={FormIncrementalSearch}
                      astrike={true}
                      validator={requiredArrayValidator}
                      fetchIncrementalData={(search: any) =>
                        handleItemSearchChange(
                          search,
                          `item_options`,
                          formRenderProps
                        )
                      }
                      options={
                        formRenderProps
                          .valueGetter("item_options")
                          ?.map((item: any) => {
                            return {
                              value: item?.id,
                              label: item?.product_name,
                            };
                          }) || []
                      }
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <Field
                      wrapperClassName="w-100"
                      name="grn_no"
                      label="GRN No"
                      placeholder="GRN No"
                      component={FormSelectionField}
                      options={
                        formRenderProps.valueGetter("grnNoOptions") || []
                      }
                    />
                  </GridLayoutItem>
                  <GridLayoutItem>
                    <div style={{ marginTop: "35px" }}>
                      <ButtonWithLoading
                        label={"View"}
                        type="submit"
                        disabled={!formRenderProps.allowSubmit || loading}
                        loading={loading}
                      />
                    </div>
                  </GridLayoutItem>
                </GridLayout>
              </FormElement>
            )}
          />
        </ShadowCard>
        {loading ? (
          <LoadingPanel gridRef={gridRef} />
        ) : (
          ItemDetailsRegisterReportList &&
          ItemDetailsRegisterReportList.length > 0 && (
            <ShadowCard style={{ marginTop: 10, minHeight: "30vh" }}>
              <ItemDetailsRegisterExportTable />
            </ShadowCard>
          )
        )}
      </GridLayoutItem>
    </GridLayout>
  );
};

const ItemDetailsRegisterExportTable: React.FC = () => {
  const isExcelAccess = checkAcessRights(
    location.pathname,
    EXCEL_EXPORT_ACCESS
  );
  const ItemDetailsRegisterReportList = useAppSelector(
    (state) => state.reports.ItemDetailsRegisterReportList
  );
  const ItemDetailsRegisterReportDetails = useAppSelector(
    (state) => state.reports.ItemDetailsRegisterReportDetails
  );

  const isPrintAccess = checkAcessRights(location.pathname, PRINT_ACCESS);
  const handlePrint = (props: any, type: string) => {
    const state = {
      from_date: props?.from_date
        ? moment(props?.from_date).format("YYYY-MM-DD")
        : "",
      to_date: props?.to_date
        ? moment(props?.to_date).format("YYYY-MM-DD")
        : "",
      item_id: props?.item_id ? props?.item_id : null,
      grn_no: props?.grn_no ? `${props?.grn_no}` : null,
      initialValues: props.initialValues,
    };

    if (type === "itemdetailsregisterreport") {
      sessionStorage.setItem(
        "itemdetailsregisterreportState",
        JSON.stringify(state)
      );
      const reportTab = window.open(
        "/itemdetailsregisterreportprint",
        "_blank"
      );
      const interval = setInterval(() => {
        if (reportTab?.closed) {
          sessionStorage.removeItem("itemdetailsregisterreportState");
          clearInterval(interval);
        }
      }, 1000);
    }
  };

  const handleExportExcel = () => {
    const datetime = moment().format("DD-MM-YYYY HH-mm");
    const table = document.querySelector(
      ".employee-details-report-table"
    ) as HTMLTableElement;
    const wb = XLSX.utils.book_new();
    const ws = XLSX.utils.table_to_sheet(table);

    // Adjust column widths dynamically
    const colWidths: number[] = [];
    const rows = table.querySelectorAll("tr");

    rows.forEach((row) => {
      const cells = row.querySelectorAll("th, td");
      cells.forEach((cell, colIndex) => {
        const cellContent = cell.textContent || "";
        const cellLength = cellContent.length;

        // Calculate width (max of existing or current cell)
        colWidths[colIndex] = Math.max(
          colWidths[colIndex] || 10,
          cellLength + 2
        );
      });
    });

    // Apply column widths
    ws["!cols"] = colWidths.map((width) => ({ wch: width }));
    const headerRows = table.querySelectorAll("thead tr");
    headerRows.forEach((row) => {
      const cells = row.querySelectorAll("th");
      cells.forEach((cell, colIndex) => {
        const cellRef = XLSX.utils.encode_cell({ r: 0, c: colIndex }); // Assume <thead> is row 0
        if (ws[cellRef]) {
          ws[cellRef].s = {
            font: { bold: true }, // Bold font
            alignment: { horizontal: "center", vertical: "center" }, // Center alignment
            fill: { fgColor: { rgb: "D9E1F2" } }, // Light background color
            border: {
              top: { style: "thin", color: { rgb: "000000" } },
              bottom: { style: "thin", color: { rgb: "000000" } },
              left: { style: "thin", color: { rgb: "000000" } },
              right: { style: "thin", color: { rgb: "000000" } },
            },
          };
        }
      });
    });

    Object.keys(ws).forEach((key) => {
      if (!key.startsWith("!")) {
        const cell = ws[key];
        if (cell.z !== "m/d/yy") {
          if (typeof cell.v === "number") {
            const isDate = cell.t === "d";
            if (!isDate) {
              cell.t = "s";
              cell.v = `${cell.v}`;
            }
          }
        } else if (cell.z === "m/d/yy") {
          cell.z = "DD-MMM-YYYY";
        }
      }
    });

    XLSX.utils.book_append_sheet(wb, ws, `Sheet1`);

    const excelBuffer = XLSX.write(wb, { bookType: "xlsx", type: "array" });
    const data = new Blob([excelBuffer], {
      type: "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet",
    });
    saveAs(data, `Item Details Register Report ${datetime}`);
  };
  const [showfilterColumns, setShowFilterColumns] = React.useState(false);
  const initialValues = {
    filter_columns: [
      "date",
      "warehouse_name",
      "process_name",
      "quantity",
      "loss_quantity",
    ],
  };
  const columns = [
    { label: "Date", value: "date" },
    { label: "Warehouse Name", value: "warehouse_name" },
    { label: "Department Name", value: "process_name" },
    { label: "Quantity", value: "quantity" },
    { label: "Loss Qty", value: "loss_quantity" },
  ];

  const checkInclude = (key: string, formRenderProps: FormRenderProps) => {
    return formRenderProps.valueGetter("filter_columns")?.includes(key);
  };
  return (
    <>
      {ItemDetailsRegisterReportList && ItemDetailsRegisterReportList.length ? (
        <Form
          initialValues={initialValues}
          render={(formRenderProps: FormRenderProps) => {
            return (
              <FormElement style={{ width: "100%" }}>
                <GridLayout
                  //   style={{ marginRight: 30 }}
                  gap={{ rows: 10, cols: 10 }}
                  cols={[{ width: "100%" }]}
                >
                  <GridLayoutItem className="d-flex justify-content-between align-items-center">
                    <Typography.h4 className="m-0">
                      Item Details Register Report
                      <>
                        <p style={{ fontSize: 16, margin: 0 }}>
                          For Period :-
                          {ItemDetailsRegisterReportDetails.from_date &&
                            ItemDetailsRegisterReportDetails.to_date &&
                            `( ${moment(
                              ItemDetailsRegisterReportDetails.from_date
                            ).format("DD/MM/YYYY")} - ${moment(
                              ItemDetailsRegisterReportDetails.to_date
                            ).format("DD/MM/YYYY")} )`}
                        </p>
                      </>
                    </Typography.h4>
                    <div className="position-relative d-flex">
                      {isPrintAccess && (
                        <Button
                          style={{ marginRight: 5 }}
                          type="button"
                          fillMode={"solid"}
                          themeColor={"primary"}
                          onClick={() =>
                            handlePrint(
                              {
                                ...ItemDetailsRegisterReportDetails,
                                initialValues: {
                                  filter_columns:
                                    formRenderProps.valueGetter(
                                      "filter_columns"
                                    ),
                                },
                              },
                              "itemdetailsregisterreport"
                            )
                          }
                        >
                          Print
                        </Button>
                      )}
                      {isExcelAccess && (
                        <Button
                          type="button"
                          fillMode={"solid"}
                          themeColor={"primary"}
                          onClick={handleExportExcel}
                          style={{ marginRight: 10 }}
                        >
                          Excel Export
                        </Button>
                      )}
                      <Tooltip
                        anchorElement="target"
                        position="left"
                        parentTitle={true}
                      >
                        <Button
                          title="Filter Columns"
                          type="button"
                          themeColor={"primary"}
                          fillMode={"solid"}
                          onClick={() => setShowFilterColumns(true)}
                        >
                          Filter
                          <BsThreeDotsVertical />
                        </Button>
                      </Tooltip>
                      {showfilterColumns && (
                        <div
                          style={{
                            position: "absolute",
                            top: "32px",
                            right: "0",
                            background: "white",
                            padding: "12px",
                            border: "1px solid whitesmoke",
                            width: "250px",
                            zIndex: 1,
                          }}
                        >
                          <div className="d-flex justify-content-between align-items-baseline">
                            <p className="m-0 fw-600">Filter</p>
                            <Button
                              themeColor={"primary"}
                              fillMode={"flat"}
                              onClick={() => setShowFilterColumns(false)}
                            >
                              <RxCross2 />
                            </Button>
                          </div>
                          <hr className="m-1" style={{ color: "lightgray" }} />
                          <Field
                            wrapperClassName="d-flex flex-column checkBoxGroup-vertical"
                            inputClassName="checkBoxGroup-vertical-input"
                            id={"filter_columns"}
                            name={"filter_columns"}
                            marginRight={0}
                            marginBeetween={8}
                            component={FormCheckboxGroup}
                            options={columns}
                          />
                        </div>
                      )}
                    </div>
                  </GridLayoutItem>
                  <GridLayoutItem
                    style={{
                      maxHeight: "calc(100vh - 340px)",
                      overflow: "scroll",
                      scrollbarWidth: "thin",
                    }}
                  >
                    <table
                      cellSpacing={1}
                      cellPadding={2}
                      className="table table-bordered w-100  employee-details-report-table"
                      style={{
                        marginTop: 1,
                      }}
                    >
                      <thead>
                        <tr
                          className="text-center fw-600"
                          style={{
                            fontSize: 12,
                            position: "sticky",
                            top: 0,
                          }}
                        >
                          {checkInclude("date", formRenderProps) && (
                            <>
                              <th style={{ minWidth: 150 }}>Date</th>
                            </>
                          )}
                          <th style={{ minWidth: 100 }}>No</th>
                          {checkInclude("warehouse_name", formRenderProps) && (
                            <>
                              <th style={{ minWidth: 250 }}>Warehouse Name</th>
                            </>
                          )}

                          {checkInclude("process_name", formRenderProps) && (
                            <>
                              <th style={{ minWidth: 200 }}>Department Name</th>
                            </>
                          )}
                          {checkInclude("quantity", formRenderProps) && (
                            <>
                              <th style={{ minWidth: 150 }}>Quantity</th>
                            </>
                          )}

                          {checkInclude("loss_quantity", formRenderProps) && (
                            <>
                              <th style={{ minWidth: 150 }}>Loss Qty</th>
                            </>
                          )}
                        </tr>
                      </thead>
                      {/* Table Body */}
                      <tbody>
                        {ItemDetailsRegisterReportList.map(
                          (item: any, rowIndex: number) => (
                            <>
                              <tr key={rowIndex}>
                                {checkInclude("date", formRenderProps) && (
                                  <td>
                                    {item?.date
                                      ? moment(item?.date, "DD/MM/YYYY").format(
                                          "DD-MMM-YYYY"
                                        )
                                      : ""}
                                  </td>
                                )}
                                <td>
                                  <div className="text-center">{item?.No}</div>
                                </td>

                                {checkInclude(
                                  "warehouse_name",
                                  formRenderProps
                                ) && <td>{item?.warehouse_name}</td>}
                                {checkInclude(
                                  "process_name",
                                  formRenderProps
                                ) && <td>{item?.process_name}</td>}
                                {checkInclude("quantity", formRenderProps) && (
                                  <td className="text-end">
                                    {item?.quantity
                                      ? formatIndianNumberForQty(item?.quantity)
                                      : ""}
                                  </td>
                                )}
                                {checkInclude(
                                  "loss_quantity",
                                  formRenderProps
                                ) && (
                                  <td className="text-end">
                                    {item?.loss_quantity
                                      ? formatIndianNumberForQty(
                                          item?.loss_quantity
                                        )
                                      : ""}
                                  </td>
                                )}
                              </tr>
                            </>
                          )
                        )}
                      </tbody>
                    </table>
                  </GridLayoutItem>
                </GridLayout>
              </FormElement>
            );
          }}
        />
      ) : (
        <AlertBox />
      )}
    </>
  );
};

export default ItemDetailsRegisterReportForm;
